/**
 * Texas ZIP code validation.
 *
 * We only sell and ship inside Texas, so the ZIP a shopper types is the
 * cheapest, most reliable signal that an address is actually in state — city
 * names repeat across states ("Paris, TX" vs "Paris, TN") but ZIP ranges do
 * not. Used on both the client (instant feedback in the cart) and the server
 * (authoritative gate before a checkout session is created).
 */

/** Contiguous ZIP ranges assigned to Texas by USPS. */
const TEXAS_ZIP_RANGES: ReadonlyArray<readonly [number, number]> = [
  [73301, 73301], // Austin — IRS
  [73344, 73344], // Austin — IRS
  [75001, 75501],
  [75502, 79999], // Texarkana through El Paso / Panhandle
  [88510, 88595], // El Paso overflow ZIPs
];

/** Strips spaces/dashes and keeps the 5-digit base of a US ZIP+4. */
export function normalizeZip(raw: string): string {
  const digits = (raw || "").replace(/[^0-9]/g, "");
  return digits.slice(0, 5);
}

/** True when the value is a well-formed 5-digit US ZIP. */
export function isValidUsZip(raw: string): boolean {
  return /^\d{5}$/.test(normalizeZip(raw));
}

/** True when the ZIP falls inside a Texas USPS range. */
export function isTexasZip(raw: string): boolean {
  const zip = normalizeZip(raw);
  if (!/^\d{5}$/.test(zip)) return false;
  const n = Number(zip);
  return TEXAS_ZIP_RANGES.some(([lo, hi]) => n >= lo && n <= hi);
}

/**
 * Pulls the most likely ZIP out of a free-text address line so a non-Texas
 * ZIP typed into the street field is caught too (e.g. "12 Main St, Lake
 * Charles, LA 70601").
 */
export function extractZip(text: string): string | null {
  const matches = (text || "").match(/\b\d{5}(?:-\d{4})?\b/g);
  if (!matches?.length) return null;
  return normalizeZip(matches[matches.length - 1]);
}

/**
 * Validates a shipping ZIP for shop orders.
 * Returns null when acceptable, or a shopper-facing message when it isn't.
 */
export function validateTexasZip(raw: string): string | null {
  const zip = normalizeZip(raw);
  if (!zip) return "Please add your Texas ZIP code.";
  if (!/^\d{5}$/.test(zip)) return "Enter a 5-digit ZIP code (for example 78155).";
  if (!isTexasZip(zip)) {
    return `ZIP ${zip} isn't in Texas. We ship to Texas (TX) addresses only — you're welcome to pick up free at our Seguin shop, or send us a request and we'll help another way.`;
  }
  return null;
}
